import React, { useEffect, useState } from 'react';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import { Box, Typography } from '@mui/material';

// Styles
import './styles.scss';

/**
 * Choose which table columns are shown. `columns` are the `{ field, label }` a screen lets users
 * show or hide; `visibleColumns` are the fields shown now and `defaultVisibleColumns` the ones
 * "Reset to default" selects (optional columns are not among them). Changes are only applied on Save
 * (`onSave` gets the checked fields, in column order); Cancel or closing discards them. At least
 * `minVisibleColumns` columns stay checked.
 */
const OlcCustomizeColumnsModal = ({
  open,
  onClose,
  columns,
  visibleColumns,
  defaultVisibleColumns,
  minVisibleColumns,
  saving = false,
  onSave,
}) => {
  const allFields = columns.map(({ field }) => field);
  const [selectedFields, setSelectedFields] = useState(visibleColumns);

  // Every time the modal opens it starts from the columns currently shown.
  useEffect(() => {
    if (open) {
      setSelectedFields(visibleColumns);
    }
    // Only opening should reset the draft, not a change of the saved columns behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isAtMinimum = selectedFields.length <= minVisibleColumns;

  const handleToggle = (field) => {
    setSelectedFields((current) =>
      current.includes(field) ? current.filter((item) => item !== field) : [...current, field]
    );
  };

  const handleSave = () => {
    onSave(allFields.filter((field) => selectedFields.includes(field)));
  };

  const { CUSTOMIZE_COLUMNS } = MESSAGES;

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={CUSTOMIZE_COLUMNS.TITLE}
      className="customizeColumnsModalWrapper"
      footer={
        <>
          <OlcButton
            variant="secondary"
            className="resetColumnsBtn"
            disabled={saving}
            onClick={() => setSelectedFields(defaultVisibleColumns)}
          >
            {CUSTOMIZE_COLUMNS.RESET}
          </OlcButton>
          <OlcButton variant="secondary" onClick={onClose} disabled={saving}>
            {CUSTOMIZE_COLUMNS.CANCEL}
          </OlcButton>
          <OlcButton variant="primary" onClick={handleSave} disabled={saving}>
            {CUSTOMIZE_COLUMNS.SAVE}
          </OlcButton>
        </>
      }
    >
      <Typography className="columnsNote">
        {CUSTOMIZE_COLUMNS.MIN_COLUMNS_NOTE(minVisibleColumns)}
      </Typography>
      <Box className="columnsList">
        {columns.map(({ field, label }) => {
          const isChecked = selectedFields.includes(field);
          // The last columns can't be unchecked; a checked box is the only one that gets locked.
          const isLocked = isChecked && isAtMinimum;

          return (
            <label key={field} className={`columnOption ${isLocked ? 'columnOptionLocked' : ''}`}>
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isLocked}
                onChange={() => handleToggle(field)}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </Box>
    </OlcFormModal>
  );
};

export default OlcCustomizeColumnsModal;
